import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Icon } from '@iconify/react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import FormControlLabel from '@mui/material/FormControlLabel'
import Paper from '@mui/material/Paper'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import Stack from '@mui/material/Stack'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Typography from '@mui/material/Typography'
import { currentWarning, resolveText, type AttemptView, type ExamQuestionView, type GivenAnswer, type SaveAnswersInput } from '@jupi/shared'
import { RemoteContent, Title, formatDate, formatDateTime, useSnackbar } from '@jupi/ui'
import { examKeys, useAttempt, useSaveAnswers, useSubmitAttempt } from '../../api/exams'
import { RESULT_COLOR, resultKey } from './examLabels'
import { clockText, useCountdown } from './useCountdown'

// Sitting an exam, and reading its result afterwards. The server is the
// authority on the clock and on marking: the countdown here runs on the
// server's time and the page submits itself a few seconds before the deadline
// so the answers count, but a submit that is late is refused by the API.

type Answers = Record<string, GivenAnswer>

const AUTOSAVE_MS = 700
// Submit this long before the deadline so the answers are not refused as late.
const AUTO_SUBMIT_SECONDS = 3

export default function ExamAttemptPage() {
  const { id } = useParams()
  const attempt = useAttempt(id)
  const view = attempt.data
  return (
    <RemoteContent isLoading={attempt.isLoading} error={attempt.error}>
      {view ? view.attempt.status === 'in_progress' ? <Sitting key={view.attempt.attemptId} view={view} /> : <ResultView view={view} /> : null}
    </RemoteContent>
  )
}

// --- sitting ---------------------------------------------------------------

const draftKey = (attemptId: string) => `exam-draft:${attemptId}`

function loadDraft(attemptId: string): Answers {
  try {
    return JSON.parse(localStorage.getItem(draftKey(attemptId)) ?? '{}') as Answers
  } catch {
    return {}
  }
}

const isAnswered = (a: GivenAnswer | undefined) => a !== undefined && (Boolean(a.choiceId) || typeof a.ticked === 'boolean')

function Sitting({ view }: { view: AttemptView }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { showSnackbar } = useSnackbar()
  const attemptId = view.attempt.attemptId
  const { resumable, timeLimitMinutes } = view.exam
  const isQuiz = view.exam.kind === 'quiz'

  const [answers, setAnswers] = useState<Answers>(() => ({ ...(resumable ? {} : loadDraft(attemptId)), ...view.answers }))
  const [step, setStep] = useState(0)
  const [confirming, setConfirming] = useState(false)
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'failed'>('idle')

  const save = useSaveAnswers(attemptId)
  const submit = useSubmitAttempt(attemptId)
  const saved = useRef<Answers>(view.answers)

  // The exam follows the member's profile language; a text with no
  // translation in it falls back to Finnish.
  const { i18n } = useTranslation()
  const tx = (text: Record<string, string>) => resolveText(text, i18n.language.slice(0, 2), 'fi')

  const answeredCount = view.questions.filter((q) => isAnswered(answers[q.questionId])).length
  const unanswered = view.questions.length - answeredCount

  const setAnswer = (questionId: string, answer: GivenAnswer) => setAnswers((prev) => ({ ...prev, [questionId]: answer }))

  // A single sitting keeps its answers only here, until it is submitted: a
  // reload or a dropped connection resumes the attempt with them.
  useEffect(() => {
    if (resumable) return
    try {
      localStorage.setItem(draftKey(attemptId), JSON.stringify(answers))
    } catch {
      /* private mode: the answers live in the page only */
    }
  }, [answers, attemptId, resumable])

  // Autosave for a resumable exam: only what changed since the last save.
  useEffect(() => {
    if (!resumable) return
    const changed = Object.entries(answers).filter(([qid, a]) => JSON.stringify(saved.current[qid]) !== JSON.stringify(a))
    if (changed.length === 0) return
    const timer = setTimeout(() => {
      setSaveState('saving')
      save.mutate(
        { answers: changed.map(([questionId, a]) => ({ questionId, choiceId: a.choiceId ?? null, ticked: a.ticked ?? null })) },
        {
          onSuccess: () => {
            saved.current = { ...saved.current, ...Object.fromEntries(changed) }
            setSaveState('saved')
          },
          onError: () => setSaveState('failed'),
        },
      )
    }, AUTOSAVE_MS)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers, resumable])

  const doSubmit = useCallback(() => {
    if (submit.isPending) return
    setConfirming(false)
    // A checklist item left blank is "not done", said explicitly.
    const body = view.questions.flatMap<SaveAnswersInput['answers'][number]>((q) => {
      const a = answers[q.questionId]
      if (q.kind === 'tick') return [{ questionId: q.questionId, ticked: a?.ticked ?? false, choiceId: null }]
      return a?.choiceId ? [{ questionId: q.questionId, choiceId: a.choiceId, ticked: null }] : []
    })
    submit.mutate(
      { answers: body },
      {
        onSuccess: () => {
          try {
            localStorage.removeItem(draftKey(attemptId))
          } catch {
            /* ignore */
          }
        },
        onError: () => {
          // Late, or already closed by the clock: show what the server decided.
          void queryClient.invalidateQueries({ queryKey: examKeys.attempt(attemptId) })
          showSnackbar(t('exams.take.submitFailed'), { severity: 'error' })
        },
      },
    )
  }, [answers, attemptId, queryClient, showSnackbar, submit, t, view.questions])

  const left = useCountdown(view.expiresAt, view.serverNow)
  const autoSubmitted = useRef(false)
  useEffect(() => {
    if (left === null || left > AUTO_SUBMIT_SECONDS || autoSubmitted.current) return
    autoSubmitted.current = true
    doSubmit()
  }, [left, doSubmit])

  const elapsed = timeLimitMinutes !== null && left !== null ? timeLimitMinutes * 60 - left : 0
  const warning = timeLimitMinutes !== null ? currentWarning(timeLimitMinutes, elapsed) : null
  const [dismissed, setDismissed] = useState<string | null>(null)

  const blockedBySittingRule = !resumable && isQuiz && unanswered > 0

  return (
    <Box>
      <Title label={tx(view.exam.title)} />

      <Paper variant="outlined" sx={{ position: 'sticky', top: { xs: 56, sm: 64 }, zIndex: 5, p: 1.5, mb: 2 }}>
        <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
          {left !== null ? (
            <Chip
              icon={<Icon icon="mdi:timer-outline" width={20} />}
              color={warning?.kind === 'lastFive' ? 'error' : warning ? 'warning' : 'default'}
              label={clockText(left)}
              aria-label={t('exams.take.timeLeft')}
              sx={{ fontVariantNumeric: 'tabular-nums', fontSize: '1.05rem' }}
            />
          ) : (
            <Chip variant="outlined" label={t('exams.untimed')} />
          )}
          <Typography variant="body2" color="text.secondary">
            {t('exams.take.answered', { done: answeredCount, total: view.questions.length })}
            {resumable && saveState !== 'idle' ? ` · ${t(`exams.take.save.${saveState}`)}` : ''}
          </Typography>
        </Stack>
        {warning && dismissed !== warning.kind ? (
          <Alert severity={warning.kind === 'lastFive' ? 'error' : 'warning'} sx={{ mt: 1 }} onClose={() => setDismissed(warning.kind)}>
            {t(`exams.take.warning.${warning.kind}`)}
          </Alert>
        ) : null}
      </Paper>

      {isQuiz ? (
        <QuizStep view={view} answers={answers} step={step} setStep={setStep} setAnswer={setAnswer} tx={tx} />
      ) : (
        <Checklist view={view} answers={answers} setAnswer={setAnswer} tx={tx} />
      )}

      <Stack direction="row" sx={{ mt: 3, justifyContent: 'flex-end' }}>
        <Button variant="contained" size="large" onClick={() => setConfirming(true)} disabled={submit.isPending || blockedBySittingRule}>
          {t('exams.take.submit')}
        </Button>
      </Stack>
      {blockedBySittingRule ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'right' }}>
          {t('exams.take.singleSittingHint', { count: unanswered })}
        </Typography>
      ) : null}

      <Dialog open={confirming} onClose={() => setConfirming(false)}>
        <DialogTitle>{t('exams.take.confirmTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {unanswered > 0
              ? t(isQuiz ? 'exams.take.confirmUnansweredQuiz' : 'exams.take.confirmUnansweredChecklist', { count: unanswered })
              : t('exams.take.confirmAll')}
          </DialogContentText>
          <DialogContentText sx={{ mt: 1 }}>{t('exams.take.confirmFinal')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(false)}>{t('exams.take.keepGoing')}</Button>
          <Button variant="contained" onClick={doSubmit}>
            {t('exams.take.submit')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

interface PartProps {
  view: AttemptView
  answers: Answers
  setAnswer: (questionId: string, answer: GivenAnswer) => void
  tx: (text: Record<string, string>) => string
}

function QuizStep({ view, answers, step, setStep, setAnswer, tx }: PartProps & { step: number; setStep: (n: number) => void }) {
  const { t } = useTranslation()
  const question = view.questions[step] as ExamQuestionView
  const total = view.questions.length
  const section = view.sections.find((s) => s.sectionId === question.sectionId)
  return (
    <Box>
      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.75, mb: 2 }} role="group" aria-label={t('exams.take.questions')}>
        {view.questions.map((q, i) => (
          <Chip
            key={q.questionId}
            label={i + 1}
            size="small"
            color={i === step ? 'primary' : isAnswered(answers[q.questionId]) ? 'success' : 'default'}
            variant={i === step || isAnswered(answers[q.questionId]) ? 'filled' : 'outlined'}
            onClick={() => setStep(i)}
            sx={{ minWidth: 36 }}
          />
        ))}
      </Stack>
      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
        {section ? (
          <Typography variant="overline" color="text.secondary">
            {tx(section.title)}
          </Typography>
        ) : null}
        <Typography variant="body2" color="text.secondary">
          {t('exams.take.questionOf', { current: step + 1, total })}
        </Typography>
        <Typography variant="h6" component="h2" sx={{ my: 1.5 }}>
          {tx(question.prompt)}
        </Typography>
        <RadioGroup
          value={answers[question.questionId]?.choiceId ?? ''}
          onChange={(e) => setAnswer(question.questionId, { choiceId: e.target.value })}
          aria-label={tx(question.prompt)}
        >
          {question.choices.map((c) => (
            <FormControlLabel
              key={c.choiceId}
              value={c.choiceId}
              control={<Radio sx={{ p: '2px', mr: 1 }} />}
              label={tx(c.label)}
              sx={{ alignItems: 'flex-start', py: 0.75, ml: 0, mr: 0 }}
            />
          ))}
        </RadioGroup>
      </Paper>
      <Stack direction="row" sx={{ mt: 2, justifyContent: 'space-between' }}>
        <Button disabled={step === 0} onClick={() => setStep(step - 1)} startIcon={<Icon icon="mdi:chevron-left" />}>
          {t('exams.take.previous')}
        </Button>
        <Button disabled={step === total - 1} onClick={() => setStep(step + 1)} endIcon={<Icon icon="mdi:chevron-right" />}>
          {t('exams.take.next')}
        </Button>
      </Stack>
    </Box>
  )
}

function Checklist({ view, answers, setAnswer, tx }: PartProps) {
  const { t } = useTranslation()
  const groups = [
    ...view.sections.map((s) => ({ key: s.sectionId, title: tx(s.title), items: view.questions.filter((q) => q.sectionId === s.sectionId) })),
    { key: 'none', title: '', items: view.questions.filter((q) => !q.sectionId) },
  ].filter((g) => g.items.length > 0)
  return (
    <Stack spacing={3}>
      {groups.map((g) => (
        <Box key={g.key}>
          {g.title ? (
            <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
              {g.title}
            </Typography>
          ) : null}
          <Stack spacing={1}>
            {g.items.map((q) => (
              <Paper key={q.questionId} variant="outlined" sx={{ p: 1.5 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} sx={{ gap: 1.5, alignItems: { sm: 'center' }, justifyContent: 'space-between' }}>
                  <Typography>{tx(q.prompt)}</Typography>
                  <ToggleButtonGroup
                    exclusive
                    size="large"
                    value={typeof answers[q.questionId]?.ticked === 'boolean' ? String(answers[q.questionId]?.ticked) : null}
                    onChange={(_, v: string | null) => v !== null && setAnswer(q.questionId, { ticked: v === 'true' })}
                    aria-label={tx(q.prompt)}
                    sx={{ flexShrink: 0, '& .MuiToggleButton-root': { minWidth: 72, minHeight: 48 } }}
                  >
                    <ToggleButton value="false" color="error">
                      {t('exams.take.no')}
                    </ToggleButton>
                    <ToggleButton value="true" color="success">
                      {t('exams.take.yes')}
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
              </Paper>
            ))}
          </Stack>
        </Box>
      ))}
    </Stack>
  )
}

// --- result ----------------------------------------------------------------

function ResultView({ view }: { view: AttemptView }) {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const tx = (text: Record<string, string>) => resolveText(text, i18n.language.slice(0, 2), 'fi')
  const a = view.attempt
  const isQuiz = view.exam.kind === 'quiz'
  const key = view.correctChoices

  return (
    <Box>
      <Title label={tx(view.exam.title)} />
      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Chip size="medium" color={a.result ? RESULT_COLOR[a.result] : 'default'} label={t(resultKey(a.status, a.result))} />
          {a.status === 'expired' ? <Chip variant="outlined" label={t('exams.status.expired')} /> : null}
        </Stack>
        {isQuiz && a.score !== null && a.maxScore !== null ? (
          <Typography variant="h5" sx={{ mt: 2 }}>
            {t('exams.resultView.score', { score: a.score, max: a.maxScore, pct: Math.floor((a.score * 100) / Math.max(a.maxScore, 1)) })}
          </Typography>
        ) : null}
        <Typography sx={{ mt: 1 }}>{t(`exams.resultView.${a.result ?? 'pending'}`, { pct: view.exam.passMarkPct })}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t('exams.resultView.when', { date: formatDateTime(a.submittedAt ?? a.startedAt) })}
          {a.validUntil ? ` · ${t('exams.validUntil', { date: formatDate(a.validUntil) })}` : ''}
        </Typography>
        {a.signedByName && a.signedAt ? (
          <Typography variant="body2" color="text.secondary">
            {t('exams.resultView.signedBy', { name: a.signedByName, date: formatDateTime(a.signedAt) })}
            {a.signNote ? ` — ${a.signNote}` : ''}
          </Typography>
        ) : null}
      </Paper>

      <Stack spacing={1}>
        {view.questions.map((q, i) => (
          <ResultRow key={q.questionId} index={i + 1} q={q} answer={view.answers[q.questionId]} correctChoiceId={key?.[q.questionId]} tx={tx} />
        ))}
      </Stack>

      <Stack direction="row" sx={{ mt: 3, gap: 1 }}>
        <Button variant="contained" onClick={() => navigate('/club/exams')}>
          {t('exams.resultView.back')}
        </Button>
      </Stack>
    </Box>
  )
}

function ResultRow({
  index,
  q,
  answer,
  correctChoiceId,
  tx,
}: {
  index: number
  q: ExamQuestionView
  answer: GivenAnswer | undefined
  correctChoiceId: string | undefined
  tx: (text: Record<string, string>) => string
}) {
  const { t } = useTranslation()
  if (q.kind === 'tick') {
    const done = answer?.ticked === true
    return (
      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Stack direction="row" sx={{ gap: 1, alignItems: 'center' }}>
          <Icon icon={done ? 'mdi:check-circle' : 'mdi:close-circle-outline'} width={22} color={done ? 'green' : 'gray'} />
          <Typography>{tx(q.prompt)}</Typography>
        </Stack>
      </Paper>
    )
  }
  const picked = q.choices.find((c) => c.choiceId === answer?.choiceId)
  const right = picked && picked.choiceId === correctChoiceId
  const correct = q.choices.find((c) => c.choiceId === correctChoiceId)
  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Stack direction="row" sx={{ gap: 1, alignItems: 'flex-start' }}>
        <Icon icon={right ? 'mdi:check-circle' : 'mdi:close-circle'} width={22} color={right ? 'green' : 'crimson'} style={{ flexShrink: 0, marginTop: 2 }} />
        <Box>
          <Typography>
            {index}. {tx(q.prompt)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('exams.resultView.yourAnswer')}: {picked ? tx(picked.label) : t('exams.resultView.noAnswer')}
          </Typography>
          {!right && correct ? (
            <Typography variant="body2" color="success.main">
              {t('exams.resultView.correctAnswer')}: {tx(correct.label)}
            </Typography>
          ) : null}
        </Box>
      </Stack>
    </Paper>
  )
}
